import { useState, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api, formatApiError } from "@/lib/api";
import { formatHour, formatPrice } from "@/lib/utils";
import { Check, X, DollarSign, RefreshCw, User, Download, List, Eye, ChevronDown, MoreHorizontal } from "lucide-react";
import { toast } from "sonner";
import BookingDrawer from "@/components/owner/BookingDrawer";

function isPaidStatus(ps) {
  return ps && !ps.startsWith("pending");
}

function displayStatus(booking) {
  if (booking.status === "checked_in") return "checked_in";
  const paid = isPaidStatus(booking.payment_status);
  const endHour = booking.end_hour;
  const dateStr = booking.date;
  const now = new Date();
  const bookingEnd = new Date(dateStr + "T" + String(Math.floor(endHour)).padStart(2, "0") + ":" + String(Math.round((endHour % 1) * 60)).padStart(2, "0") + ":00");
  if (booking.status === "confirmed" && paid && bookingEnd < now) return "completed";
  return booking.status;
}

const STATUS_STYLES = {
  confirmed: "border-volt text-volt bg-volt/10",
  pending: "border-yellow-400 text-yellow-400 bg-yellow-400/10",
  cancelled: "border-muted-foreground/40 text-muted-foreground/80 bg-border/30",
  rejected: "border-red-400 text-red-400 bg-red-400/10",
  checked_in: "border-sky-400 text-sky-400 bg-sky-400/10",
  completed: "border-emerald-400 text-emerald-400 bg-emerald-400/10",
};

export default function BookingsTab({ bookings, onRefresh }) {
  const { t } = useTranslation();
  const [refreshing, setRefreshing] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const filterStatus = searchParams.get("status") || "all";
  const searchQuery = searchParams.get("q") || "";
  const dateFrom = searchParams.get("from") || "";
  const dateTo = searchParams.get("to") || "";
  const sortBy = searchParams.get("sort") || "created_at";
  const sortDir = searchParams.get("dir") || "desc";
  const pageFromUrl = parseInt(searchParams.get("page") || "0", 10);
  const [presetKey, setPresetKey] = useState(null);
  const [page, setPage] = useState(pageFromUrl);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [actionsOpen, setActionsOpen] = useState(null); // booking id or null
  const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);
  const [sortDropdownOpen, setSortDropdownOpen] = useState(false);
  const [dateDropdownOpen, setDateDropdownOpen] = useState(false);
  const [overflowOpen, setOverflowOpen] = useState(false);
  const perPage = 25;

  // Sync page to URL
  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    if (page === 0) next.delete("page"); else next.set("page", String(page));
    const nextStr = next.toString();
    const curStr = searchParams.toString();
    if (nextStr !== curStr) setSearchParams(next, { replace: true });
  }, [page]);

  // Helper to update a filter in URL
  const updateFilter = (key, value) => {
    const next = new URLSearchParams(searchParams);
    if (!value || value === "all" || value === "created_at" || value === "desc") {
      next.delete(key);
    } else {
      next.set(key, value);
    }
    // Reset to default values when deleting
    if (key === "sort" && (!value || value === "created_at")) next.delete("sort");
    if (key === "dir" && (!value || value === "desc")) next.delete("dir");
    setSearchParams(next, { replace: true });
    setPage(0);
  };

  const handleRefLookupByUid = async (uid) => {
    if (!uid) return;
    try {
      const { data } = await api.get(`/bookings/ref/${uid}`);
      setSelectedBooking(data);
      updateFilter("q", "");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Booking not found");
    }
  };

  const sortOptions = [
    { key: "created_at", label: "owner.sort_created" },
    { key: "date", label: "owner.sort_date" },
    { key: "total_price", label: "owner.sort_total" },
    { key: "user_name", label: "owner.sort_player" },
  ];

  const getSortValue = (b) => {
    switch (sortBy) {
      case "date": return b.date;
      case "created_at": return b.created_at || "";
      case "total_price": return b.total_price;
      case "user_name": return (b.user_name || "").toLowerCase();
      default: return b.created_at || "";
    }
  };

  const d = new Date();
  const today = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  const weekStart = (() => { const d = new Date(); d.setDate(d.getDate() - d.getDay()); const y = d.getFullYear(); const m = String(d.getMonth() + 1).padStart(2, "0"); const day = String(d.getDate()).padStart(2, "0"); return `${y}-${m}-${day}`; })();
  const monthStart = today.slice(0, 7) + "-01";
  const presets = [
    { key: "today", label: "owner.today_label", from: today, to: today },
    { key: "week", label: "owner.week_label", from: weekStart, to: today },
    { key: "month", label: "owner.month_label", from: monthStart, to: today },
  ];

  const filteredBookings = bookings.filter((b) => {
    if (filterStatus !== "all" && displayStatus(b) !== filterStatus) return false;
    if (dateFrom && b.date < dateFrom) return false;
    if (dateTo && b.date > dateTo) return false;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      const name = (b.user_name || "").toLowerCase();
      const email = (b.user_email || "").toLowerCase();
      const phone = (b.user_phone || "").toLowerCase();
      const court = (b.court_name || "").toLowerCase();
      if (!name.includes(q) && !email.includes(q) && !phone.includes(q) && !court.includes(q)) return false;
    }
    return true;
  });

  const sortedBookings = [...filteredBookings].sort((a, b) => {
    const va = getSortValue(a);
    const vb = getSortValue(b);
    if (va < vb) return sortDir === "asc" ? -1 : 1;
    if (va > vb) return sortDir === "asc" ? 1 : -1;
    return 0;
  });

  const totalPages = Math.ceil(sortedBookings.length / perPage) || 1;
  const safePage = Math.min(page, totalPages - 1);
  const paginatedBookings = sortedBookings.slice(safePage * perPage, (safePage + 1) * perPage);

  useEffect(() => { setPage(0); }, [filterStatus, dateFrom, dateTo, sortBy, sortDir, searchQuery]);

  const decide = async (id, decision) => {
    try {
      await api.patch(`/bookings/${id}/decision`, null, { params: { decision } });
      toast.success(decision === "confirmed" ? t("common.booking_approved") : t("common.booking_rejected"));
      onRefresh();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const markPaid = async (id) => {
    try {
      await api.patch(`/bookings/${id}/mark-paid`);
      toast.success(t("common.booking_marked_paid"));
      onRefresh();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const checkIn = async (id) => {
    try {
      await api.post(`/bookings/${id}/checkin`);
      toast.success(t("owner.checked_in"));
      onRefresh();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const checkOut = async (id) => {
    try {
      await api.post(`/bookings/${id}/checkout`);
      toast.success(t("owner.checked_out"));
      onRefresh();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const handleCliqConfirm = async (bookingId) => {
    try {
      await api.post(`/bookings/${bookingId}/cliq-confirm`);
      toast.success(t("owner.cliq_confirmed"));
      onRefresh();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    }
  };

  const handleCliqReject = async (bookingId) => {
    if (!window.confirm(t("owner.cliq_reject_confirm"))) return;
    try {
      await api.post(`/bookings/${bookingId}/cliq-reject`);
      toast.success(t("owner.cliq_rejected_success"));
      onRefresh();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    }
  };

  const handleCancel = async (bookingId) => {
    const reason = window.prompt(t("owner.cancel_reason_prompt") || "Reason for cancellation:");
    if (!reason) return;
    try {
      await api.post(`/bookings/${bookingId}/owner-cancel`, { cancellation_reason: reason });
      toast.success(t("owner.booking_cancelled"));
      onRefresh();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    const pending = paginatedBookings.filter((b) => b.status === "pending");
    const allSelected = pending.every((b) => selectedIds.has(b.id));
    setSelectedIds((prev) => {
      const next = new Set(prev);
      for (const b of pending) {
        if (allSelected) next.delete(b.id); else next.add(b.id);
      }
      return next;
    });
  };

  const bulkDecide = async (decision) => {
    const ids = [...selectedIds].filter((id) => paginatedBookings.find((b) => b.id === id)?.status === "pending");
    if (!ids.length) return;
    try {
      await api.patch("/owner/bookings/bulk-decision", { ids, decision });
      toast.success(t("owner.bulk_" + decision, `${ids.length} bookings ${decision}`));
      setSelectedIds(new Set());
      onRefresh();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await onRefresh();
    setRefreshing(false);
  };

  const exportCsv = async () => {
    try {
      const r = await api.get('/owner/bookings/export', {
        params: { from: dateFrom || undefined, to: dateTo || undefined },
        responseType: 'blob',
      });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url;
      const d = new Date(); a.download = `bookings-export-${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const selectedPending = paginatedBookings.filter((b) => selectedIds.has(b.id) && b.status === "pending");

  if (bookings.length === 0) {
    return (
      <div className="border border-dashed border-border/30 p-16 text-center">
        <List className="h-8 w-8 mx-auto text-muted-foreground/40 mb-3" />
        <p className="text-muted-foreground/80 text-sm">{t("bookings.none")}</p>
      </div>
    );
  }

  return (
    <>
      <div className="flex items-center gap-2 mb-4 flex-wrap p-3 border border-border/40 bg-card rounded-md">
        {/* Search */}
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => { const v = e.target.value; updateFilter("q", v); }}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              const uid = searchQuery.trim().toUpperCase();
              if (uid.startsWith("BKG-")) {
                e.preventDefault();
                handleRefLookupByUid(uid);
              }
            }
          }}
          placeholder={t("owner.search_or_ref")}
          className="bg-background border border-input px-3 py-1.5 text-[10px] tracking-widest uppercase text-muted-foreground/80 font-bold focus:outline-none focus:border-volt w-48"
        />

        <span className="w-px h-5 bg-border/40 hidden md:block" />

        {/* Status filter */}
        <div className="flex gap-1 relative">
          <button
            onClick={() => setStatusDropdownOpen(!statusDropdownOpen)}
            className="flex items-center gap-1.5 px-3 py-1.5 text-[10px] tracking-widest uppercase font-bold border border-border hover:border-volt/50 transition-colors bg-background justify-between min-w-[100px]"
          >
            <span>
              <span className="text-muted-foreground/50 font-normal">Status </span>
              <span className={filterStatus !== "all" ? "text-volt" : "text-muted-foreground/80"}>
                {filterStatus === "all" ? t("owner.all") : t("bookings.status_" + filterStatus, filterStatus)}
              </span>
            </span>
            <ChevronDown className={`h-3 w-3 text-muted-foreground/60 transition-transform ${statusDropdownOpen ? "rotate-180" : ""}`} />
          </button>
          {statusDropdownOpen && (
            <>
              <div className="fixed inset-0 z-20" onClick={() => setStatusDropdownOpen(false)} />
              <div className="absolute top-full mt-1 z-30 bg-card border border-border shadow-lg rounded-sm w-40 py-1">
                {["all", "pending", "confirmed", "completed", "cancelled", "rejected"].map((s) => (
                  <button
                    key={s}
                    onClick={() => { updateFilter("status", s); setPage(0); setStatusDropdownOpen(false); }}
                    className={`w-full text-left px-3 py-2 text-[10px] tracking-widest uppercase font-bold transition-colors ${
                      filterStatus === s ? "bg-volt text-black" : "text-muted-foreground/80 hover:bg-muted hover:text-foreground"
                    }`}
                  >
                    {s === "all" ? t("owner.all") : t("bookings.status_" + s, s)}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Date filter */}
        <div className="flex gap-1 relative">
          <button
            onClick={() => setDateDropdownOpen(!dateDropdownOpen)}
            className="flex items-center gap-1.5 px-3 py-1.5 text-[10px] tracking-widest uppercase font-bold border border-border hover:border-volt/50 transition-colors bg-background justify-between min-w-[100px]"
          >
            <span>
              <span className="text-muted-foreground/50 font-normal">Date </span>
              <span className={presetKey ? "text-volt" : "text-muted-foreground/80"}>
                {presetKey && presetKey !== "custom" ? t(presets.find(p => p.key === presetKey)?.label || "") : (dateFrom || dateTo ? t("owner.custom_range") : t("owner.all_time") || "All")}
              </span>
            </span>
            <ChevronDown className={`h-3 w-3 text-muted-foreground/60 transition-transform ${dateDropdownOpen ? "rotate-180" : ""}`} />
          </button>
          {dateDropdownOpen && (
            <>
              <div className="fixed inset-0 z-20" onClick={() => setDateDropdownOpen(false)} />
              <div className="absolute top-full mt-1 z-30 bg-card border border-border shadow-lg rounded-sm w-40 py-1">
                <button onClick={() => {
                  setSearchParams((prev) => { const next = new URLSearchParams(prev); next.delete("from"); next.delete("to"); return next; }, { replace: true });
                  setPresetKey(null); setPage(0); setDateDropdownOpen(false);
                }} className={`w-full text-left px-3 py-2 text-[10px] tracking-widest uppercase font-bold transition-colors ${!presetKey && !dateFrom && !dateTo ? "bg-volt text-black" : "text-muted-foreground/80 hover:bg-muted hover:text-foreground"}`}>
                  {t("owner.all")}
                </button>
                {presets.map((preset) => (
                  <button key={preset.key} onClick={() => {
                    setSearchParams((prev) => { const next = new URLSearchParams(prev); if (preset.from) next.set("from", preset.from); else next.delete("from"); if (preset.to) next.set("to", preset.to); else next.delete("to"); return next; }, { replace: true });
                    setPresetKey(preset.key); setPage(0); setDateDropdownOpen(false);
                  }} className={`w-full text-left px-3 py-2 text-[10px] tracking-widest uppercase font-bold transition-colors ${presetKey === preset.key ? "bg-volt text-black" : "text-muted-foreground/80 hover:bg-muted hover:text-foreground"}`}>
                    {t(preset.label)}
                  </button>
                ))}
                <div className="border-t border-border/30 mt-1 pt-1">
                  <button onClick={() => setDateDropdownOpen(false)} className="w-full text-left px-3 py-2 text-[10px] tracking-widest uppercase font-bold transition-colors text-muted-foreground/80 hover:bg-muted hover:text-foreground">
                    {t("owner.custom_range")}
                  </button>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Date range inputs */}
        <input
          type="date"
          value={dateFrom}
          onChange={(e) => { updateFilter("from", e.target.value); setPresetKey(null); }}
          className="bg-background border border-input px-2 py-1.5 text-[10px] tracking-widest uppercase text-muted-foreground/80 font-bold focus:outline-none focus:border-volt w-[130px]"
        />
        <span className="text-[10px] text-muted-foreground/40">–</span>
        <input
          type="date"
          value={dateTo}
          onChange={(e) => { updateFilter("to", e.target.value); setPresetKey(null); }}
          className="bg-background border border-input px-2 py-1.5 text-[10px] tracking-widest uppercase text-muted-foreground/80 font-bold focus:outline-none focus:border-volt w-[130px]"
        />

        {/* Clear dates */}
        {(dateFrom || dateTo) && (
          <button onClick={() => { setSearchParams((prev) => { const next = new URLSearchParams(prev); next.delete("from"); next.delete("to"); return next; }, { replace: true }); setPresetKey(null); setPage(0); }}
            className="text-[10px] tracking-widest uppercase text-yellow-400 hover:underline font-bold">
            {t("owner.clear_date")}
          </button>
        )}

        <span className="w-px h-5 bg-border/40 hidden md:block" />

        {/* Sort filter */}
        <div className="flex gap-1 relative">
          <button
            onClick={() => setSortDropdownOpen(!sortDropdownOpen)}
            className="flex items-center gap-1.5 px-3 py-1.5 text-[10px] tracking-widest uppercase font-bold border border-border hover:border-volt/50 transition-colors bg-background justify-between min-w-[120px]"
          >
            <span>
              <span className="text-muted-foreground/50 font-normal">Sort </span>
              {t(sortOptions.find(o => o.key === sortBy)?.label || "")}
              <span className="text-volt ml-1">{sortDir === "asc" ? "↑" : "↓"}</span>
            </span>
            <ChevronDown className={`h-3 w-3 text-muted-foreground/60 transition-transform ${sortDropdownOpen ? "rotate-180" : ""}`} />
          </button>
          {sortDropdownOpen && (
            <>
              <div className="fixed inset-0 z-20" onClick={() => setSortDropdownOpen(false)} />
              <div className="absolute top-full mt-1 z-30 bg-card border border-border shadow-lg rounded-sm w-44 py-1">
                {sortOptions.map((opt) => (
                  <button key={opt.key} onClick={() => {
                    if (sortBy === opt.key) { updateFilter("dir", sortDir === "asc" ? "desc" : "asc"); }
                    else { setSearchParams((prev) => { const next = new URLSearchParams(prev); next.set("sort", opt.key); next.delete("dir"); return next; }, { replace: true }); setPage(0); }
                    setSortDropdownOpen(false);
                  }} className={`w-full text-left px-3 py-2 text-[10px] tracking-widest uppercase font-bold transition-colors flex items-center justify-between ${sortBy === opt.key ? "bg-volt text-black" : "text-muted-foreground/80 hover:bg-muted hover:text-foreground"}`}>
                    {t(opt.label)}
                    {sortBy === opt.key && <span>{sortDir === "asc" ? "↑" : "↓"}</span>}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Actions */}
        <button onClick={handleRefresh} className="flex items-center gap-1 px-3 py-1.5 text-[10px] tracking-widest uppercase font-bold border border-border hover:border-volt/50 transition-colors bg-background">
          <RefreshCw className={`h-3 w-3 ${refreshing ? "animate-spin" : ""}`} /> {t("common.refresh")}
        </button>

        {/* Overflow menu */}
        <div className="relative">
          <button
            onClick={() => setOverflowOpen(!overflowOpen)}
            className="flex items-center px-2 py-1.5 border border-border hover:border-volt/50 transition-colors bg-background"
          >
            <MoreHorizontal className="h-3.5 w-3.5 text-muted-foreground/80" />
          </button>
          {overflowOpen && (
            <>
              <div className="fixed inset-0 z-20" onClick={() => setOverflowOpen(false)} />
              <div className="absolute right-0 top-full mt-1 z-30 bg-card border border-border shadow-lg rounded-sm w-44 py-1">
                <button onClick={() => { exportCsv(); setOverflowOpen(false); }} className="w-full text-left px-3 py-2 text-[10px] tracking-widest uppercase font-bold text-muted-foreground/80 hover:bg-muted hover:text-foreground transition-colors flex items-center gap-2">
                  <Download className="h-3 w-3" /> {t("owner.export_csv")}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
      {selectedIds.size > 0 && (
        <div className="sticky top-0 z-10 bg-background border-b border-volt/30 py-3 mb-3 transition-all">
          <div className="flex items-center gap-3 px-4">
          <span className="text-[10px] tracking-widest uppercase font-bold text-volt">{selectedPending.length} {t("owner.selected")}</span>
          <button
            onClick={() => bulkDecide("confirmed")}
            className="bg-volt text-primary-foreground px-3 py-1.5 text-[10px] tracking-widest uppercase font-bold flex items-center gap-1.5"
          >
            <Check className="h-3 w-3" /> {t("owner.approve_all")}
          </button>
          <button
            onClick={() => bulkDecide("rejected")}
            className="border border-red-400 text-red-400 px-3 py-1.5 text-[10px] tracking-widest uppercase font-bold flex items-center gap-1.5 hover:bg-red-400 hover:text-primary-foreground transition-colors"
          >
            <X className="h-3 w-3" /> {t("owner.reject_all")}
          </button>
          <button
            onClick={() => setSelectedIds(new Set())}
            className="text-[10px] tracking-widest uppercase text-muted-foreground/60 hover:text-muted-foreground/80 ml-auto"
          >
            {t("owner.clear_selection")}
          </button>
          </div>
        </div>
      )}
      <div className="border border-border bg-card overflow-hidden">
        {/* Desktop table — hidden below md */}
        <div className="hidden md:block overflow-x-auto -mx-4 md:mx-0">
          <table className="w-full min-w-[900px] text-sm">
            <thead className="bg-background">
              <tr>
                <th className="p-3 w-10">
                  <input
                    type="checkbox"
                    checked={paginatedBookings.filter((b) => b.status === "pending").length > 0 && paginatedBookings.filter((b) => b.status === "pending").every((b) => selectedIds.has(b.id))}
                    onChange={toggleSelectAll}
                    className="accent-volt"
                  />
                </th>
                {[t("owner.bookings_table.player"), t("owner.bookings_table.court"), t("owner.bookings_table.date"), t("owner.bookings_table.time"), t("owner.bookings_table.total"), t("owner.bookings_table.extras"), t("owner.bookings_table.status"), t("owner.bookings_table.payment"), t("owner.bookings_table.action")].map((h) => (
                  <th key={h} className="text-start p-3 text-[10px] tracking-widest uppercase text-muted-foreground/80 font-bold whitespace-nowrap">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {paginatedBookings.length === 0 ? (
                <tr>
                  <td colSpan={10} className="p-10 text-center text-muted-foreground/60 text-sm">
                    {t("bookings.none")}
                  </td>
                </tr>
              ) : (
                paginatedBookings.map((b) => {
                  const payLabel = t(`bookings.payment.${b.payment_status}`, b.payment_status);
                  const statusKey = displayStatus(b);
                  return (
                  <tr key={b.id} className="border-t border-border/30 cursor-pointer hover:bg-volt/5 transition-colors" data-testid={`owner-booking-${b.id}`} onClick={() => setSelectedBooking(b)}>
                    <td className="p-3" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={selectedIds.has(b.id)}
                        onChange={() => toggleSelect(b.id)}
                        className="accent-volt"
                        disabled={b.status !== "pending"}
                      />
                    </td>
                    <td className="p-3 whitespace-nowrap">{b.user_name}{b.is_manual && <span className="ml-1.5 inline-block text-[9px] tracking-wider uppercase font-bold px-1.5 py-0.5 bg-volt/15 text-volt rounded-sm">Manual</span>}</td>
                    <td className="p-3 whitespace-nowrap">{b.court_name}</td>
                    <td className="p-3 whitespace-nowrap">{b.date}</td>
                    <td className="p-3 font-mono text-xs whitespace-nowrap">
                      {formatHour(b.start_hour)}-{formatHour(b.end_hour)}
                    </td>
                    <td className="p-3 text-volt font-bold whitespace-nowrap">{formatPrice(b.total_price, b.currency)}</td>
                    <td className="p-3 whitespace-nowrap">
                      {b.selected_extras && b.selected_extras.length > 0 ? (
                        <span className="text-[10px] tracking-widest uppercase font-bold text-yellow-400/80 border border-yellow-400/30 px-1.5 py-0.5">
                          +{b.selected_extras.length}
                        </span>
                      ) : (
                        <span className="text-[10px] text-muted-foreground/40">—</span>
                      )}
                    </td>
                    <td className="p-3 whitespace-nowrap">
                      <span className={`text-[10px] uppercase tracking-widest border px-1.5 py-0.5 font-bold ${STATUS_STYLES[statusKey] || "border-border text-muted-foreground/80"}`}>
                        {t("bookings.status_" + statusKey, statusKey)}
                      </span>
                    </td>
                    <td className="p-3 whitespace-nowrap">
                      {b.payment_status !== 'cliQ_pending' && b.payment_status !== 'cliQ_rejected' && (
                        <span className={`text-[10px] uppercase tracking-widest ${
                          b.payment_status === "paid_at_court" ? "text-volt" :
                          b.payment_status === "pending_cod" || b.payment_status === "pending_online" ? "text-yellow-400" :
                          "text-emerald-400"
                        }`}>
                          {payLabel}
                        </span>
                      )}
                      {b.payment_status === 'cliQ_pending' && (
                        <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 border border-yellow-400 text-yellow-400 bg-yellow-400/10 ml-2">
                          {t("owner.cliq_pending")}
                        </span>
                      )}
                      {b.payment_status === 'cliQ_rejected' && (
                        <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 border border-red-400 text-red-400 bg-red-400/10 ml-2">
                          {t("owner.cliq_rejected")}
                        </span>
                      )}
                    </td>
                    <td className="p-3 relative">
                      {(b.payment_status === 'cliQ_pending' || b.payment_status === 'cliQ_rejected' || b.payment_status === "pending_cod" || b.status === "pending" || b.status === "confirmed" || b.status === "checked_in") && (
                        <>
                          <button
                            onClick={(e) => { e.stopPropagation(); setActionsOpen(actionsOpen === b.id ? null : b.id); }}
                            className="btn-outline rounded-sm px-3 py-1.5 text-[10px] tracking-widest uppercase font-bold whitespace-nowrap hover:border-volt hover:text-volt transition-colors"
                          >
                            <List className="h-3 w-3 inline mr-1" />
                            {t("owner.show_actions")}
                          </button>

                          {actionsOpen === b.id && (
                            <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={(e) => { e.stopPropagation(); setActionsOpen(null); }}>
                              <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" />
                              <div className="relative bg-card border border-border/50 shadow-2xl rounded-lg w-80 z-10" onClick={(e) => e.stopPropagation()}>
                                {/* Header */}
                                <div className="flex items-center justify-between p-4 border-b border-border/30">
                                  <div className="min-w-0">
                                    <div className="text-sm font-semibold truncate text-foreground/90">{b.user_name}</div>
                                    <div className="text-[10px] text-muted-foreground/60 truncate tracking-wider uppercase">{b.court_name} · {b.date}</div>
                                  </div>
                                  <button onClick={(e) => { e.stopPropagation(); setActionsOpen(null); }} className="text-muted-foreground/40 hover:text-foreground transition-colors ml-2 shrink-0">
                                    <X className="h-4 w-4" />
                                  </button>
                                </div>

                                <div className="p-4 space-y-4">
                                  {/* Payment section */}
                                  {(b.payment_status === 'cliQ_pending' && b.cliQ_proof_url || b.payment_status === 'cliQ_rejected' && b.cliQ_proof_url || b.payment_status === "pending_cod" && b.status !== "cancelled" && b.status !== "rejected") && (
                                    <div className="border border-border/40 bg-background/50 rounded-md p-3">
                                      <div className="text-[9px] tracking-[0.2em] uppercase text-muted-foreground mb-3 font-semibold">{t("owner.section_payment")}</div>
                                      <div className="flex flex-col gap-1.5">
                                        {b.payment_status === 'cliQ_pending' && b.cliQ_proof_url && (
                                          <>
                                            <button onClick={() => { window.open(b.cliQ_proof_url.replace(/[?&]w=\d+|[?&]h=\d+/g, '').replace(/^\?&/, '?'), '_blank'); setActionsOpen(null); }}
                                              className="w-full text-left border border-sky-400/40 bg-sky-400/5 text-sky-400 hover:bg-sky-400 hover:text-white transition-all px-3 py-2 rounded text-[11px] tracking-widest uppercase font-bold flex items-center gap-2">
                                              <Eye className="h-3.5 w-3.5 shrink-0" /> {t("owner.view_proof")}
                                            </button>
                                            <button onClick={() => { handleCliqConfirm(b.id); setActionsOpen(null); }}
                                              className="w-full text-left bg-volt text-black hover:bg-volt/80 transition-all px-3 py-2 rounded text-[11px] tracking-widest uppercase font-bold flex items-center gap-2">
                                              <Check className="h-3.5 w-3.5 shrink-0" /> {t("owner.confirm_payment")}
                                            </button>
                                            <button onClick={() => { handleCliqReject(b.id); setActionsOpen(null); }}
                                              className="w-full text-left border border-red-400/40 bg-red-400/5 text-red-400 hover:bg-red-400 hover:text-white transition-all px-3 py-2 rounded text-[11px] tracking-widest uppercase font-bold flex items-center gap-2">
                                              <X className="h-3.5 w-3.5 shrink-0" /> {t("owner.reject_payment")}
                                            </button>
                                          </>
                                        )}
                                        {b.payment_status === 'cliQ_rejected' && b.cliQ_proof_url && (
                                          <button onClick={() => { window.open(b.cliQ_proof_url.replace(/[?&]w=\d+|[?&]h=\d+/g, '').replace(/^\?&/, '?'), '_blank'); setActionsOpen(null); }}
                                            className="w-full text-left border border-sky-400/40 bg-sky-400/5 text-sky-400 hover:bg-sky-400 hover:text-white transition-all px-3 py-2 rounded text-[11px] tracking-widest uppercase font-bold flex items-center gap-2">
                                            <Eye className="h-3.5 w-3.5 shrink-0" /> {t("owner.view_proof")}
                                          </button>
                                        )}
                                        {b.payment_status === "pending_cod" && b.status !== "cancelled" && b.status !== "rejected" && (
                                          <button onClick={() => { markPaid(b.id); setActionsOpen(null); }}
                                            className="w-full text-left border border-emerald-400/40 bg-emerald-400/5 text-emerald-400 hover:bg-emerald-400 hover:text-white transition-all px-3 py-2 rounded text-[11px] tracking-widest uppercase font-bold flex items-center gap-2">
                                            <DollarSign className="h-3.5 w-3.5 shrink-0" /> {t("owner.mark_paid")}
                                          </button>
                                        )}
                                      </div>
                                    </div>
                                  )}

                                  {/* Booking section */}
                                  {(b.status === "pending" || b.status === "confirmed" || b.status === "checked_in") && (
                                    <div className="border border-border/40 bg-background/50 rounded-md p-3">
                                      <div className="text-[9px] tracking-[0.2em] uppercase text-muted-foreground mb-3 font-semibold">{t("owner.section_booking")}</div>
                                      <div className="flex flex-col gap-1.5">
                                        {b.status === "pending" && (
                                          <>
                                            <button onClick={() => { decide(b.id, "confirmed"); setActionsOpen(null); }}
                                              className="w-full text-left bg-volt text-black hover:bg-volt/80 transition-all px-3 py-2 rounded text-[11px] tracking-widest uppercase font-bold flex items-center gap-2">
                                              <Check className="h-3.5 w-3.5 shrink-0" /> {t("owner.approve")}
                                            </button>
                                            <button onClick={() => { decide(b.id, "rejected"); setActionsOpen(null); }}
                                              className="w-full text-left border border-red-400/40 bg-red-400/5 text-red-400 hover:bg-red-400 hover:text-white transition-all px-3 py-2 rounded text-[11px] tracking-widest uppercase font-bold flex items-center gap-2">
                                              <X className="h-3.5 w-3.5 shrink-0" /> {t("owner.reject")}
                                            </button>
                                          </>
                                        )}
                                        {b.status === "confirmed" && (
                                          <>
                                            <button onClick={() => { checkIn(b.id); setActionsOpen(null); }}
                                              className="w-full text-left border border-sky-400/40 bg-sky-400/5 text-sky-400 hover:bg-sky-400 hover:text-white transition-all px-3 py-2 rounded text-[11px] tracking-widest uppercase font-bold flex items-center gap-2">
                                              <User className="h-3.5 w-3.5 shrink-0" /> {t("owner.check_in")}
                                            </button>
                                            <button onClick={() => { handleCancel(b.id); setActionsOpen(null); }}
                                              className="w-full text-left border border-red-400/40 bg-red-400/5 text-red-400 hover:bg-red-400 hover:text-white transition-all px-3 py-2 rounded text-[11px] tracking-widest uppercase font-bold flex items-center gap-2">
                                              <X className="h-3.5 w-3.5 shrink-0" /> {t("owner.cancel_booking")}
                                            </button>
                                          </>
                                        )}
                                        {b.status === "checked_in" && (
                                          <button onClick={() => { checkOut(b.id); setActionsOpen(null); }}
                                            className="w-full text-left border border-emerald-400/40 bg-emerald-400/5 text-emerald-400 hover:bg-emerald-400 hover:text-white transition-all px-3 py-2 rounded text-[11px] tracking-widest uppercase font-bold flex items-center gap-2">
                                            <Check className="h-3.5 w-3.5 shrink-0" /> {t("owner.check_out")}
                                          </button>
                                        )}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          )}
                        </>
                      )}
                    </td>
                  </tr>
                );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Mobile card layout — visible only below md */}
        <div className="md:hidden divide-y divide-border/30">
          {paginatedBookings.length === 0 ? (
            <div className="p-10 text-center text-muted-foreground/60 text-sm">
              {t("bookings.none")}
            </div>
          ) : (
            paginatedBookings.map((b) => {
              const payLabel = t(`bookings.payment.${b.payment_status}`, b.payment_status);
              const statusKey = displayStatus(b);
              return (
                <div
                  key={b.id}
                  className="p-3 cursor-pointer hover:bg-volt/5 transition-colors"
                  data-testid={`owner-booking-${b.id}`}
                  onClick={() => setSelectedBooking(b)}
                >
                  {/* Top row: checkbox + status badge + price */}
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={selectedIds.has(b.id)}
                        onChange={() => toggleSelect(b.id)}
                        className="accent-volt"
                        disabled={b.status !== "pending"}
                        onClick={(e) => e.stopPropagation()}
                      />
                      <span className={`text-[10px] uppercase tracking-widest border px-1.5 py-0.5 font-bold ${STATUS_STYLES[statusKey] || "border-border text-muted-foreground/80"}`}>
                        {t("bookings.status_" + statusKey, statusKey)}
                      </span>
                    </div>
                    <span className="text-volt font-bold">{formatPrice(b.total_price, b.currency)}</span>
                  </div>

                  {/* Court name */}
                  <div className="text-base font-semibold">{b.court_name}</div>

                  {/* Date • Time */}
                  <div className="text-xs text-muted-foreground mt-1">
                    {b.date}  •  <span className="font-mono">{formatHour(b.start_hour)}–{formatHour(b.end_hour)}</span>
                  </div>

                  {/* Player */}
                  <div className="text-sm mt-1">
                    {b.user_name}
                    {b.is_manual && <span className="ml-1.5 inline-block text-[9px] tracking-wider uppercase font-bold px-1.5 py-0.5 bg-volt/15 text-volt rounded-sm">Manual</span>}
                  </div>

                  {/* Payment + extras */}
                  <div className="flex items-center gap-2 mt-2">
                    {b.payment_status !== 'cliQ_pending' && b.payment_status !== 'cliQ_rejected' && (
                      <span className={`text-[10px] uppercase tracking-widest ${
                        b.payment_status === "paid_at_court" ? "text-volt" :
                        b.payment_status === "pending_cod" || b.payment_status === "pending_online" ? "text-yellow-400" :
                        "text-emerald-400"
                      }`}>
                        {payLabel}
                      </span>
                    )}
                    {b.payment_status === 'cliQ_pending' && (
                      <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 border border-yellow-400 text-yellow-400 bg-yellow-400/10">
                        {t("owner.cliq_pending")}
                      </span>
                    )}
                    {b.payment_status === 'cliQ_rejected' && (
                      <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 border border-red-400 text-red-400 bg-red-400/10">
                        {t("owner.cliq_rejected")}
                      </span>
                    )}
                    {b.selected_extras && b.selected_extras.length > 0 && (
                      <span className="text-[10px] tracking-widest uppercase font-bold text-yellow-400/80 border border-yellow-400/30 px-1.5 py-0.5">
                        +{b.selected_extras.length} {t("owner.bookings_table.extras")}
                      </span>
                    )}
                  </div>

                  {/* Action buttons */}
                  {(b.payment_status === 'cliQ_pending' || b.payment_status === 'cliQ_rejected' || b.payment_status === "pending_cod" || b.status === "pending" || b.status === "confirmed" || b.status === "checked_in") && (
                    <div className="relative mt-2">
                      <button
                        onClick={(e) => { e.stopPropagation(); setActionsOpen(actionsOpen === b.id ? null : b.id); }}
                        className="btn-outline rounded-sm px-3 py-1.5 text-[10px] tracking-widest uppercase font-bold whitespace-nowrap hover:border-volt hover:text-volt transition-colors"
                      >
                        <List className="h-3 w-3 inline mr-1" />
                        {t("owner.show_actions")}
                      </button>

                      {actionsOpen === b.id && (
                        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={(e) => { e.stopPropagation(); setActionsOpen(null); }}>
                          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" />
                          <div className="relative bg-card border border-border/50 shadow-2xl rounded-lg w-80 z-10" onClick={(e) => e.stopPropagation()}>
                            {/* Header */}
                            <div className="flex items-center justify-between p-4 border-b border-border/30">
                              <div className="min-w-0">
                                <div className="text-sm font-semibold truncate text-foreground/90">{b.user_name}</div>
                                <div className="text-[10px] text-muted-foreground/60 truncate tracking-wider uppercase">{b.court_name} · {b.date}</div>
                              </div>
                              <button onClick={(e) => { e.stopPropagation(); setActionsOpen(null); }} className="text-muted-foreground/40 hover:text-foreground transition-colors ml-2 shrink-0">
                                <X className="h-4 w-4" />
                              </button>
                            </div>

                            <div className="p-4 space-y-4">
                              {/* Payment section */}
                              {(b.payment_status === 'cliQ_pending' && b.cliQ_proof_url || b.payment_status === 'cliQ_rejected' && b.cliQ_proof_url || b.payment_status === "pending_cod" && b.status !== "cancelled" && b.status !== "rejected") && (
                                <div className="border border-border/40 bg-background/50 rounded-md p-3">
                                  <div className="text-[9px] tracking-[0.2em] uppercase text-muted-foreground mb-3 font-semibold">{t("owner.section_payment")}</div>
                                  <div className="flex flex-col gap-1.5">
                                    {b.payment_status === 'cliQ_pending' && b.cliQ_proof_url && (
                                      <>
                                        <button onClick={() => { window.open(b.cliQ_proof_url.replace(/[?&]w=\d+|[?&]h=\d+/g, '').replace(/^\?&/, '?'), '_blank'); setActionsOpen(null); }}
                                          className="w-full text-left border border-sky-400/40 bg-sky-400/5 text-sky-400 hover:bg-sky-400 hover:text-white transition-all px-3 py-2 rounded text-[11px] tracking-widest uppercase font-bold flex items-center gap-2">
                                          <Eye className="h-3.5 w-3.5 shrink-0" /> {t("owner.view_proof")}
                                        </button>
                                        <button onClick={() => { handleCliqConfirm(b.id); setActionsOpen(null); }}
                                          className="w-full text-left bg-volt text-black hover:bg-volt/80 transition-all px-3 py-2 rounded text-[11px] tracking-widest uppercase font-bold flex items-center gap-2">
                                          <Check className="h-3.5 w-3.5 shrink-0" /> {t("owner.confirm_payment")}
                                        </button>
                                        <button onClick={() => { handleCliqReject(b.id); setActionsOpen(null); }}
                                          className="w-full text-left border border-red-400/40 bg-red-400/5 text-red-400 hover:bg-red-400 hover:text-white transition-all px-3 py-2 rounded text-[11px] tracking-widest uppercase font-bold flex items-center gap-2">
                                          <X className="h-3.5 w-3.5 shrink-0" /> {t("owner.reject_payment")}
                                        </button>
                                      </>
                                    )}
                                    {b.payment_status === 'cliQ_rejected' && b.cliQ_proof_url && (
                                      <button onClick={() => { window.open(b.cliQ_proof_url.replace(/[?&]w=\d+|[?&]h=\d+/g, '').replace(/^\?&/, '?'), '_blank'); setActionsOpen(null); }}
                                        className="w-full text-left border border-sky-400/40 bg-sky-400/5 text-sky-400 hover:bg-sky-400 hover:text-white transition-all px-3 py-2 rounded text-[11px] tracking-widest uppercase font-bold flex items-center gap-2">
                                        <Eye className="h-3.5 w-3.5 shrink-0" /> {t("owner.view_proof")}
                                      </button>
                                    )}
                                    {b.payment_status === "pending_cod" && b.status !== "cancelled" && b.status !== "rejected" && (
                                      <button onClick={() => { markPaid(b.id); setActionsOpen(null); }}
                                        className="w-full text-left border border-emerald-400/40 bg-emerald-400/5 text-emerald-400 hover:bg-emerald-400 hover:text-white transition-all px-3 py-2 rounded text-[11px] tracking-widest uppercase font-bold flex items-center gap-2">
                                        <DollarSign className="h-3.5 w-3.5 shrink-0" /> {t("owner.mark_paid")}
                                      </button>
                                    )}
                                  </div>
                                </div>
                              )}

                              {/* Booking section */}
                              {(b.status === "pending" || b.status === "confirmed" || b.status === "checked_in") && (
                                <div className="border border-border/40 bg-background/50 rounded-md p-3">
                                  <div className="text-[9px] tracking-[0.2em] uppercase text-muted-foreground mb-3 font-semibold">{t("owner.section_booking")}</div>
                                  <div className="flex flex-col gap-1.5">
                                    {b.status === "pending" && (
                                      <>
                                        <button onClick={() => { decide(b.id, "confirmed"); setActionsOpen(null); }}
                                          className="w-full text-left bg-volt text-black hover:bg-volt/80 transition-all px-3 py-2 rounded text-[11px] tracking-widest uppercase font-bold flex items-center gap-2">
                                          <Check className="h-3.5 w-3.5 shrink-0" /> {t("owner.approve")}
                                        </button>
                                        <button onClick={() => { decide(b.id, "rejected"); setActionsOpen(null); }}
                                          className="w-full text-left border border-red-400/40 bg-red-400/5 text-red-400 hover:bg-red-400 hover:text-white transition-all px-3 py-2 rounded text-[11px] tracking-widest uppercase font-bold flex items-center gap-2">
                                          <X className="h-3.5 w-3.5 shrink-0" /> {t("owner.reject")}
                                        </button>
                                      </>
                                    )}
                                    {b.status === "confirmed" && (
                                      <>
                                        <button onClick={() => { checkIn(b.id); setActionsOpen(null); }}
                                          className="w-full text-left border border-sky-400/40 bg-sky-400/5 text-sky-400 hover:bg-sky-400 hover:text-white transition-all px-3 py-2 rounded text-[11px] tracking-widest uppercase font-bold flex items-center gap-2">
                                          <User className="h-3.5 w-3.5 shrink-0" /> {t("owner.check_in")}
                                        </button>
                                        <button onClick={() => { handleCancel(b.id); setActionsOpen(null); }}
                                          className="w-full text-left border border-red-400/40 bg-red-400/5 text-red-400 hover:bg-red-400 hover:text-white transition-all px-3 py-2 rounded text-[11px] tracking-widest uppercase font-bold flex items-center gap-2">
                                          <X className="h-3.5 w-3.5 shrink-0" /> {t("owner.cancel_booking")}
                                        </button>
                                      </>
                                    )}
                                    {b.status === "checked_in" && (
                                      <button onClick={() => { checkOut(b.id); setActionsOpen(null); }}
                                        className="w-full text-left border border-emerald-400/40 bg-emerald-400/5 text-emerald-400 hover:bg-emerald-400 hover:text-white transition-all px-3 py-2 rounded text-[11px] tracking-widest uppercase font-bold flex items-center gap-2">
                                        <Check className="h-3.5 w-3.5 shrink-0" /> {t("owner.check_out")}
                                      </button>
                                    )}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>
      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-4">
          <span className="text-[10px] text-muted-foreground/60 tracking-wider uppercase">
            {safePage * perPage + 1}–{Math.min((safePage + 1) * perPage, sortedBookings.length)} / {sortedBookings.length}
          </span>
          <div className="flex gap-1">
            <button
              disabled={safePage === 0}
              onClick={() => setPage((p) => p - 1)}
              className="btn-outline rounded-sm px-3 py-1.5 text-[10px] tracking-widest uppercase font-bold disabled:opacity-30"
            >
              {t("owner.prev")}
            </button>
            <button
              disabled={safePage >= totalPages - 1}
              onClick={() => setPage((p) => p + 1)}
              className="btn-outline rounded-sm px-3 py-1.5 text-[10px] tracking-widest uppercase font-bold disabled:opacity-30"
            >
              {t("owner.next")}
            </button>
          </div>
        </div>
      )}

      <BookingDrawer
        booking={selectedBooking}
        onClose={() => setSelectedBooking(null)}
        onRefresh={onRefresh}
        t={t}
        formatPrice={formatPrice}
      />
    </>
  );
}
